/**
 * AdminContentReview — editorial approval queue for doctor-authored
 * content (case studies + testimonials). Articles auto-publish but can
 * also be unpublished from this queue via the "Published" tab.
 *
 * iter100.25 (2026-02-28)
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │  Header: stats bar (Pending · Needs changes · Published · Rej.)│
 *   ├──────────┬─────────────────────────────────────────────────────┤
 *   │  Filter  │  Queue list (cards)                                 │
 *   │  chips   │    → click a card to expand + review inline         │
 *   └──────────┴─────────────────────────────────────────────────────┘
 *
 * Actions per pending item:
 *   • Approve                → status: published
 *   • Request changes (+ comment required)
 *   • Reject         (+ reason required)
 *
 * Actions per published item:
 *   • Unpublish (kicks it back to `draft`, doctor keeps data)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Newspaper, Sparkle, ChatCircleText, CheckCircle, X, ArrowsClockwise,
    Article, Clock, Stethoscope, User, Warning, PaperPlaneTilt, Star, Quotes,
} from "@phosphor-icons/react";
import { confirmDialog } from "@/lib/confirm";

const STATUS_TABS = [
    { id: "pending_review", label: "Pending",        color: "amber" },
    { id: "needs_changes",  label: "Needs changes",  color: "orange" },
    { id: "published",      label: "Published",      color: "emerald" },
    { id: "rejected",       label: "Rejected",       color: "red" },
    // iter100.171 — Legacy audit tab. Published rows that were never
    // reviewed (mostly seeded content + articles from the auto-publish
    // era). Retroactively vet without disrupting live pages.
    { id: "audit_needed",   label: "Audit needed",   color: "sky" },
];

const KIND_META = {
    article:     { label: "Article",     icon: Newspaper },
    case_study:  { label: "Case Study",  icon: Sparkle },
    testimonial: { label: "Testimonial", icon: ChatCircleText },
};

export default function AdminContentReview() {
    const [status, setStatus] = useState("pending_review");
    const [kindFilter, setKindFilter] = useState("all"); // all | article | case_study | testimonial
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [expandedId, setExpandedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (kindFilter !== "all") params.kind = kindFilter;
            // iter100.171 — Legacy audit tab uses a distinct endpoint that
            // filters published rows without a reviewer_id.
            const listUrl = status === "audit_needed"
                ? "/admin/content-review/audit-queue"
                : "/admin/content-review/queue";
            if (status !== "audit_needed") params.status = status;
            const [{ data: list }, { data: st }] = await Promise.all([
                api.get(listUrl, { params }),
                api.get("/admin/content-review/stats"),
            ]);
            setRows(Array.isArray(list) ? list : []);
            setStats(st || {});
        } catch {
            toast.error("Could not load review queue");
        } finally {
            setLoading(false);
        }
    }, [status, kindFilter]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-content-review">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline text-primary mb-1">Editorial</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Content Review</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-snug">
                        All doctor-authored content (articles, case studies, testimonials) requires editorial approval before publishing to a doctor&apos;s public page. The <span className="font-semibold">Audit needed</span> tab shows legacy grandfathered content that was never reviewed — you can vet these retroactively without disrupting live pages.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="content-review-refresh">
                    <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </Button>
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="content-review-stats">
                {STATUS_TABS.map((s) => (
                    <StatCard
                        key={s.id}
                        label={s.label}
                        count={stats[s.id] ?? 0}
                        active={status === s.id}
                        onClick={() => { setStatus(s.id); setExpandedId(null); }}
                        color={s.color}
                        testId={`content-review-stat-${s.id}`}
                    />
                ))}
            </div>

            {/* Filter chips (by kind) */}
            <div className="flex items-center gap-2 flex-wrap border-b border-border pb-3">
                <span className="text-xs font-semibold text-muted-foreground mr-1">Filter by type:</span>
                {["all", "case_study", "testimonial", "article"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setKindFilter(k)}
                        className={`h-7 px-3 rounded-full text-[12px] font-semibold border transition-all ${
                            kindFilter === k
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-foreground border-border hover:border-primary/40"
                        }`}
                        data-testid={`content-review-kind-filter-${k}`}
                    >
                        {k === "all" ? "All types" : KIND_META[k]?.label + "s"}
                    </button>
                ))}
            </div>

            {/* Queue */}
            {loading ? (
                <div className="text-center py-16 text-muted-foreground text-sm">Loading queue…</div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-12 text-center">
                    <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <CheckCircle size={22} weight="duotone" className="text-primary" />
                    </div>
                    <div className="font-display text-lg font-bold">All caught up.</div>
                    <p className="text-sm text-muted-foreground mt-1">Nothing in <span className="font-semibold text-foreground">{STATUS_TABS.find((s) => s.id === status)?.label}</span> right now.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((row) => (
                        <ReviewCard
                            key={row.id}
                            row={row}
                            expanded={expandedId === row.id}
                            onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
                            onDone={load}
                            /* Audit rows are already `published`; treat them as
                               published for the action toolbar (Approve marks
                               them retroactively reviewed; Unpublish pulls
                               them offline). */
                            currentStatus={status === "audit_needed" ? "published" : status}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function StatCard({ label, count, active, onClick, color, testId }) {
    const bg = {
        amber:   active ? "bg-amber-500 text-white border-amber-500"     : "bg-amber-50 text-amber-800 border-amber-200",
        orange:  active ? "bg-orange-500 text-white border-orange-500"   : "bg-orange-50 text-orange-800 border-orange-200",
        emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-800 border-emerald-200",
        red:     active ? "bg-red-600 text-white border-red-600"         : "bg-red-50 text-red-800 border-red-200",
        sky:     active ? "bg-sky-600 text-white border-sky-600"         : "bg-sky-50 text-sky-800 border-sky-200",
    }[color] || "bg-slate-100 text-slate-700 border-slate-200";
    return (
        <button
            onClick={onClick}
            className={`rounded-2xl border p-4 text-left transition-all hover:shadow-sm ${bg}`}
            data-testid={testId}
        >
            <div className="text-3xl font-display font-bold leading-none">{count}</div>
            <div className={`text-[11px] uppercase tracking-wider font-bold mt-1 ${active ? "opacity-90" : "opacity-70"}`}>{label}</div>
        </button>
    );
}

function ReviewCard({ row, expanded, onToggle, onDone, currentStatus }) {
    const meta = KIND_META[row.kind] || KIND_META.article;
    const KindIcon = meta.icon;
    const data = row.data || {};
    const doctor = row.doctor || {};
    const isPublished = currentStatus === "published";

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`content-review-card-${row.id}`}>
            {/* Card header */}
            <button
                onClick={onToggle}
                className="w-full p-4 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors"
                data-testid={`content-review-toggle-${row.id}`}
            >
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <KindIcon size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-primary">{meta.label}</span>
                        {row.needs_audit && (
                            <span
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-sky-50 border-sky-200 text-sky-800"
                                title="Legacy content that was never reviewed. Approve to mark as retroactively vetted, or unpublish to pull it down."
                                data-testid={`content-review-audit-badge-${row.id}`}
                            >
                                <Warning size={10} weight="fill" /> Needs audit
                            </span>
                        )}
                        {row.submitted_at && (
                            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                <Clock size={11} weight="duotone" /> Submitted {relTime(row.submitted_at)}
                            </span>
                        )}
                    </div>
                    <div className="font-display font-bold text-base text-slate-900 leading-tight mt-0.5 truncate">
                        {data.title || data.patient_display_name || "(untitled)"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
                        {doctor.name && (
                            <Link
                                to={`/admin/doctors/${doctor.id || row.doctor_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                                data-testid={`content-review-doctor-link-${row.id}`}
                            >
                                <User size={11} weight="duotone" />
                                {doctor.name}
                            </Link>
                        )}
                        {doctor.landing_slug && (
                            <a
                                href={`/s/${doctor.landing_slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-primary transition-colors"
                            >
                                /s/{doctor.landing_slug}
                            </a>
                        )}
                    </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 mt-1">
                    {expanded ? "Hide" : "Review"} →
                </div>
            </button>

            {/* Expanded review area */}
            {expanded && (
                <div className="border-t border-border">
                    <ContentPreview row={row} />
                    <ReviewActions row={row} onDone={onDone} isPublished={isPublished} />
                </div>
            )}
        </div>
    );
}

/* Full content preview — mirrors what the doctor's public page would show. */
function ContentPreview({ row }) {
    const data = row.data || {};
    if (row.kind === "article") {
        return (
            <div className="p-5 bg-slate-50/50">
                {data.cover_image && (
                    <img src={data.cover_image} alt="" className="w-full max-h-64 object-cover rounded-xl mb-4" />
                )}
                <h3 className="font-display text-lg font-bold text-slate-900">{data.title}</h3>
                {data.published_at && <div className="text-xs text-muted-foreground mt-0.5">{data.published_at}</div>}
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{data.summary}</p>
                {data.url && (
                    <a href={data.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-primary text-sm font-semibold hover:underline break-all">
                        {data.url}
                    </a>
                )}
            </div>
        );
    }
    if (row.kind === "case_study") {
        return (
            <div className="p-5 bg-slate-50/50 space-y-3">
                <div className="flex gap-3">
                    {data.before_image && (
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Before</div>
                            <img src={data.before_image} alt="Before" className="w-full aspect-square object-cover rounded-xl border" />
                        </div>
                    )}
                    {data.after_image && (
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">After</div>
                            <img src={data.after_image} alt="After" className="w-full aspect-square object-cover rounded-xl border" />
                        </div>
                    )}
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900">{data.title}</h3>
                <div className="flex flex-wrap gap-1.5">
                    {data.concern_type && <MetaPill>{data.concern_type}</MetaPill>}
                    {data.complexity && <MetaPill>{data.complexity} complexity</MetaPill>}
                    {data.duration && <MetaPill><Clock size={11} weight="fill" /> {data.duration}</MetaPill>}
                    {data.aligner_count > 0 && <MetaPill>{data.aligner_count} aligners</MetaPill>}
                    {data.treatment && <MetaPill><Stethoscope size={11} weight="fill" /> {data.treatment}</MetaPill>}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{data.description}</p>
                <div className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${data.patient_consent_given !== false ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                    {data.patient_consent_given !== false ? <><CheckCircle size={12} weight="fill" /> Patient consent confirmed</> : <><Warning size={12} weight="fill" /> Consent NOT confirmed — needs review</>}
                </div>
            </div>
        );
    }
    if (row.kind === "testimonial") {
        return (
            <div className="p-5 bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900">{data.patient_display_name}</div>
                    {data.is_anonymous && <span className="text-[10px] font-bold bg-slate-200 text-slate-700 px-1.5 rounded uppercase">Anonymous display</span>}
                    {data.rating > 0 && (
                        <div className="flex ml-2">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} size={13} weight={n <= data.rating ? "fill" : "regular"} className={n <= data.rating ? "text-amber-500" : "text-slate-300"} />
                            ))}
                        </div>
                    )}
                </div>
                <div className="relative pl-6">
                    <Quotes size={22} weight="fill" className="absolute top-0 left-0 text-primary/30" />
                    <p className="text-sm text-slate-800 italic leading-relaxed">&ldquo;{data.quote}&rdquo;</p>
                </div>
                {(data.treatment || data.treatment_date) && (
                    <div className="text-xs text-muted-foreground">{[data.treatment, data.treatment_date].filter(Boolean).join(" · ")}</div>
                )}
            </div>
        );
    }
    return null;
}

function MetaPill({ children }) {
    return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
            {children}
        </span>
    );
}

/* Action buttons + comment input at bottom of expanded card. */
function ReviewActions({ row, onDone, isPublished }) {
    const [mode, setMode] = useState(null); // "approve" | "request_changes" | "reject" | "unpublish"
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    /* Show any prior review comment for context (from a previous rejection
       cycle etc.) — useful when a doctor re-submits after edits. */
    const hasPriorComment = !!row.review_comment;

    const post = async (path, requireComment) => {
        if (requireComment && !comment.trim()) {
            toast.error("Please provide a comment.");
            return;
        }
        if (path.endsWith("/reject")) {
            const ok = await confirmDialog({
                title: "Reject this content?",
                body: "The doctor will see the reason and cannot re-submit unless they edit.",
                confirmLabel: "Reject",
                danger: true,
            });
            if (!ok) return;
        }
        setSubmitting(true);
        try {
            await api.post(`/admin/content-review/${row.id}/${path}`, { comment: comment.trim() });
            toast.success("Done");
            setMode(null);
            setComment("");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-4 border-t border-border bg-white space-y-3">
            {hasPriorComment && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
                    <div className="font-semibold text-slate-700 mb-1">Previous reviewer note</div>
                    <div className="text-slate-600 leading-snug">{row.review_comment}</div>
                </div>
            )}

            {mode ? (
                <>
                    <Textarea
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={
                            mode === "request_changes"
                                ? "What should the doctor fix? (Required)"
                                : mode === "reject"
                                    ? "Why is this being rejected? (Required)"
                                    : "Optional note to the doctor…"
                        }
                        className="text-sm"
                        data-testid="content-review-comment"
                    />
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setMode(null); setComment(""); }} data-testid="content-review-cancel">Cancel</Button>
                        <Button
                            size="sm"
                            onClick={() => {
                                const map = { approve: ["approve", false], request_changes: ["request-changes", true], reject: ["reject", true], unpublish: ["unpublish", false] };
                                const [path, req] = map[mode];
                                post(path, req);
                            }}
                            disabled={submitting}
                            className={mode === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
                            data-testid="content-review-confirm"
                        >
                            {submitting ? "Working…" : (
                                mode === "approve" ? "Approve" :
                                mode === "request_changes" ? "Send feedback" :
                                mode === "reject" ? "Reject" :
                                "Unpublish"
                            )}
                        </Button>
                    </div>
                </>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    {isPublished ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMode("unpublish")}
                            className="text-orange-700 border-orange-200 hover:bg-orange-50"
                            data-testid="content-review-btn-unpublish"
                        >
                            <PaperPlaneTilt size={13} weight="duotone" /> Unpublish
                        </Button>
                    ) : (
                        <>
                            <Button size="sm" onClick={() => setMode("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="content-review-btn-approve">
                                <CheckCircle size={13} weight="fill" /> Approve &amp; publish
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setMode("request_changes")} className="text-orange-700 border-orange-200 hover:bg-orange-50" data-testid="content-review-btn-request-changes">
                                <PaperPlaneTilt size={13} weight="duotone" /> Request changes
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setMode("reject")} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="content-review-btn-reject">
                                <X size={13} weight="bold" /> Reject
                            </Button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function relTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const d = Date.now() - t;
    if (d < 60_000) return "just now";
    if (d < 3600_000) return `${Math.round(d / 60_000)}m ago`;
    if (d < 86400_000) return `${Math.round(d / 3600_000)}h ago`;
    return `${Math.round(d / 86400_000)}d ago`;
}
